import dayjs from 'dayjs'
import { useEffect } from 'react'
import utc from 'dayjs/plugin/utc'
import { getCPDataWithoutParams, postCPDataWithoutParams } from 'src/clinic/services'
import { getMPDataWithoutParams, postMPDataWithoutParams } from 'src/mentor/services'
import {
  BACKGROUND_REQUEST_HEADER,
  installAxiosNetworkTracking,
  waitForForegroundNetworkIdle,
} from 'src/consumer/services/networkActivityTracker'
import { primeEmrIntelForSync } from 'src/emr/intel/prescriptionIntel'
import { getDataMapFromCommonResponse } from 'src/mentor/helpers/utilityFunctions'
import {
  HE_DRUG_ADVICE_DOSAGE,
  HE_DRUG_ADVICE_DURATION,
  HE_DRUG_ADVICE_FREQUENCY,
  HE_DRUG_ADVICE_NOTE,
  HE_DRUG_ADVICE_TIMING,
  HE_DRUG_ADVICE_TYPE,
  LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED,
} from 'src/consumer/constants'
import { createERMDatabase, deleteERMDatabase, ERM_DB_VERSION } from './indexedDb'
import {
  addOrUpdateAdvicesInIdb,
  addOrUpdateClinicalEntitiesInIdb,
  addOrUpdateDiagnosisInIdb,
  addOrUpdateDrugAdviceEntitiesInIdb,
  addOrUpdateDrugsInIdb,
  addOrUpdateGenericDrugsInIdb,
  addOrUpdateHealthComplaintsInIdb,
  addOrUpdateInstructionsInIdb,
  addOrUpdateInvestigationInIdb,
  addOrUpdateObservationsInIdb,
  addOrUpdatePatientsInIdb,
  addOrUpdatePsychAssessmentsInIdb,
  clearDecryptedStoreCache,
  clearStoreInIdb,
  deleteDrugAdviceEntitiesFromIdb,
  deleteRecordsFromStore,
  DRUG_ADVICE_ENTITY_PARAMS_WITH_IDB_STORE,
  prewarmEmrSearchCaches,
} from './indexedDbDao'

dayjs.extend(utc)

// Background population of the EMR IndexedDB.
//
// Safety/resilience properties:
// - single-flight per tab, and a Web Locks guard so two tabs never sync concurrently
// - starts when the browser is idle (never competes with initial page render) and
//   waits for connectivity; yields between pages so the UI stays responsive
// - every page fetch retries with exponential backoff before giving up
// - a store's last-synced timestamp advances ONLY after that store fully synced for
//   every clinic brand — a failed run leaves the timestamp behind, so the next run
//   re-covers the missed window instead of silently losing it
// - one store failing never blocks the others; failures are collected and reported
// - re-runs periodically while the app stays open, so local data keeps tracking the
//   server without any user action

const SYNC_LOCK_NAME = 'erm-idb-background-sync'
const PAGE_SIZE = 500
const MAX_PAGE_ATTEMPTS = 3
const RETRY_BASE_DELAY_MS = 1500
const INTER_PAGE_DELAY_MS = 50
const PERIODIC_RESYNC_INTERVAL_MS = 30 * 60 * 1000
const STORAGE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'

const KEYS = LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED

// ---------------------------------------------------------------- sync control plane
// Per-store pause/resume, page cursors for clean resume of interrupted full syncs,
// remembered real page totals (accurate progress instead of estimates), and a
// subscribable live state registry for the settings "Update Local Data" panel.

const PAUSED_STORES_KEY = 'emrSyncPausedStores'
const CURSOR_KEY_PREFIX = 'emrSyncCursor__'
const KNOWN_PAGES_KEY_PREFIX = 'emrSyncPages__'

function readJsonFromStorage(key) {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch (error) {
    return null
  }
}

function writeJsonToStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    // storage full/blocked — resume just falls back to restarting the store
  }
}

function getPausedStoreKeys() {
  const stored = readJsonFromStorage(PAUSED_STORES_KEY)
  return new Set(Array.isArray(stored) ? stored : [])
}

export function isEmrSyncStorePaused(progressKey) {
  return getPausedStoreKeys().has(progressKey)
}

export function pauseEmrSyncStore(progressKey) {
  const paused = getPausedStoreKeys()
  paused.add(progressKey)
  writeJsonToStorage(PAUSED_STORES_KEY, [...paused])
  emitStoreState(progressKey, { status: 'paused' })
}

export function resumeEmrSyncStore(progressKey) {
  const paused = getPausedStoreKeys()
  paused.delete(progressKey)
  writeJsonToStorage(PAUSED_STORES_KEY, [...paused])
  emitStoreState(progressKey, { status: 'idle' })
}

const cursorStorageKey = (progressKey, clinicBrandId) =>
  `${CURSOR_KEY_PREFIX}${progressKey}__${clinicBrandId}`
const knownPagesStorageKey = (progressKey, clinicBrandId) =>
  `${KNOWN_PAGES_KEY_PREFIX}${progressKey}__${clinicBrandId}`

function anyResumeCursorExists() {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      if (localStorage.key(i)?.startsWith(CURSOR_KEY_PREFIX)) {
        return true
      }
    }
  } catch (error) {
    // inaccessible storage — treat as no cursors
  }
  return false
}

function clearStoreSyncMarkers(definition, clinicBrandIds) {
  localStorage.removeItem(definition.storageKey)
  ;(clinicBrandIds || []).forEach((clinicBrandId) => {
    localStorage.removeItem(cursorStorageKey(definition.progressKey, clinicBrandId))
    localStorage.removeItem(knownPagesStorageKey(definition.progressKey, clinicBrandId))
  })
}

// live per-store runtime state, merged over the persisted markers
const storeRuntimeState = new Map()
const syncStateListeners = new Set()

function emitStoreState(progressKey, patch) {
  const previous = storeRuntimeState.get(progressKey) || {}
  storeRuntimeState.set(progressKey, { ...previous, ...patch, updatedAt: Date.now() })
  const snapshot = getEmrSyncStoreStates()
  syncStateListeners.forEach((listener) => {
    try {
      listener(snapshot)
    } catch (error) {
      // one bad listener must not break the sync
    }
  })
}

/** Live snapshot per store: status, progress, resumability, pause flag, last synced. */
export function getEmrSyncStoreStates() {
  const paused = getPausedStoreKeys()
  const states = {}
  for (const definition of SYNC_DEFINITIONS) {
    const runtime = storeRuntimeState.get(definition.progressKey) || {}
    const cursors = (getKnownEmrSyncBrandIds() || []).map((brandId) => ({
      clinicBrandId: brandId,
      cursor: readJsonFromStorage(cursorStorageKey(definition.progressKey, brandId)),
    })).filter((c) => c.cursor?.nextPage > 1)
    states[definition.progressKey] = {
      progressKey: definition.progressKey,
      paused: paused.has(definition.progressKey),
      lastSyncedAt: localStorage.getItem(definition.storageKey) || null,
      resumeCursors: cursors,
      status: runtime.status || (cursors.length > 0 ? 'interrupted' : 'idle'),
      ...runtime,
    }
  }
  const adviceRuntime = storeRuntimeState.get('drugAdviceEntities') || {}
  states.drugAdviceEntities = {
    progressKey: 'drugAdviceEntities',
    paused: paused.has('drugAdviceEntities'),
    lastSyncedAt: localStorage.getItem(KEYS.DRUG_ADVICE_ENTITIES) || null,
    resumeCursors: [],
    status: adviceRuntime.status || 'idle',
    ...adviceRuntime,
  }
  const intelRuntime = storeRuntimeState.get('prescriptionIntel') || {}
  states.prescriptionIntel = {
    progressKey: 'prescriptionIntel',
    paused: paused.has('prescriptionIntel'),
    lastSyncedAt: localStorage.getItem(INTEL_LAST_SYNCED_KEY) || null,
    resumeCursors: [],
    status: intelRuntime.status || 'idle',
    ...intelRuntime,
  }
  return states
}

/** Subscribe the settings panel to live sync state; returns an unsubscribe. */
export function subscribeEmrSyncState(listener) {
  syncStateListeners.add(listener)
  try {
    listener(getEmrSyncStoreStates())
  } catch (error) {
    // ignore a throwing initial paint
  }
  return () => syncStateListeners.delete(listener)
}

// progressKey values intentionally match the legacy sync-dialog keys
const SYNC_DEFINITIONS = [
  {
    progressKey: 'drugs',
    storageKey: KEYS.DRUGS,
    documentType: 'drugs',
    listPath: '/api/secure/drug/list',
    writeFn: addOrUpdateDrugsInIdb,
    // ~80k legacy public drugs + ~250k A-Z India catalog drugs at PAGE_SIZE 500
    estimatedPages: 700,
    // DrugInfo is prescriber data — only doctors fetch it; other portal roles
    // (therapist/teacher/screener) would just get a @PreAuthorize 403 anyway
    requiredRoles: ['ROLE_DOCTOR'],
  },
  {
    progressKey: 'complaints',
    storageKey: KEYS.COMPLAINTS,
    documentType: 'health_complaints',
    listPath: '/api/secure/clinic-practitioner/health-complaint/list',
    writeFn: addOrUpdateHealthComplaintsInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'diagnosis',
    storageKey: KEYS.DIAGNOSIS,
    documentType: 'diagnosis',
    listPath: '/api/secure/clinic-practitioner/diagnosis/list',
    writeFn: addOrUpdateDiagnosisInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'observations',
    storageKey: KEYS.OBSERVATIONS,
    documentType: 'health_observations',
    listPath: '/api/secure/clinic-practitioner/health-observation/list',
    writeFn: addOrUpdateObservationsInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'advices',
    storageKey: KEYS.ADVICES,
    documentType: 'prescription_advice',
    listPath: '/api/secure/clinic-practitioner/prescription-advice/list',
    writeFn: addOrUpdateAdvicesInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'instruction',
    storageKey: KEYS.INSTRUCTIONS,
    documentType: 'instructions',
    listPath: '/api/secure/clinic-practitioner/instruction/list',
    writeFn: addOrUpdateInstructionsInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'investigation',
    storageKey: KEYS.INVESTIGATION,
    documentType: 'investigation',
    listPath: '/api/secure/clinic-practitioner/investigation/list',
    writeFn: addOrUpdateInvestigationInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'patients',
    storageKey: KEYS.PATIENTS,
    // must match the backend's MedicalDocCollectionUpdateStatus.PATIENT_INFO_COLLECTION_NAME —
    // this was 'patients', which never matched, so the store synced once and then skipped forever
    documentType: 'patient-info-collection-name',
    listPath: '/api/secure/patient/list',
    writeFn: addOrUpdatePatientsInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'psychAssessments',
    storageKey: KEYS.PSYCH_ASSESSMENTS,
    documentType: 'psych_assessments',
    listPath: '/api/secure/clinic-practitioner/psych-assessment/list',
    writeFn: addOrUpdatePsychAssessmentsInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'genericDrugs',
    storageKey: KEYS.GENERIC_DRUGS,
    documentType: 'drugs_generic_names',
    listPath: '/api/secure/clinic-practitioner/drug-generic-formula/list',
    writeFn: addOrUpdateGenericDrugsInIdb,
    // ~60k legacy formulas + the new A-Z catalog formulas at PAGE_SIZE 500
    estimatedPages: 300,
  },
  // ICD-10 clinical vocabulary — documentType values match the backend's
  // MedicalDocCollectionUpdateStatus constants so custom entries re-sync incrementally
  {
    progressKey: 'clinicalDiagnosis',
    storageKey: KEYS.CLINICAL_DIAGNOSIS,
    documentType: 'clinical_diagnoses',
    listPath: '/api/secure/clinic-practitioner/clinical-diagnosis/list',
    writeFn: (items) => addOrUpdateClinicalEntitiesInIdb('clinical-diagnosis', items),
    estimatedPages: 25,
  },
  {
    progressKey: 'clinicalSymptoms',
    storageKey: KEYS.CLINICAL_SYMPTOMS,
    documentType: 'clinical_symptoms',
    listPath: '/api/secure/clinic-practitioner/clinical-symptom/list',
    writeFn: (items) => addOrUpdateClinicalEntitiesInIdb('clinical-symptom', items),
    estimatedPages: 3,
  },
  {
    progressKey: 'clinicalDescriptors',
    storageKey: KEYS.CLINICAL_DESCRIPTORS,
    documentType: 'clinical_descriptors',
    listPath: '/api/secure/clinic-practitioner/clinical-descriptor/list',
    writeFn: (items) => addOrUpdateClinicalEntitiesInIdb('clinical-descriptor', items),
    estimatedPages: 2,
  },
]

// Manifest documentType (Mongo collection) for each Rx-dropdown entity, so the drug-advice group can
// be gated by medical-document-update-status like every other store. Names per the backend @Document
// collections (quirks: timing -> _when, note -> _notes, drug-info-type -> drugs_types).
const DRUG_ADVICE_PARAM_TO_DOCUMENT_TYPE = {
  [HE_DRUG_ADVICE_DOSAGE]: 'drugs_advice_dosage',
  [HE_DRUG_ADVICE_DURATION]: 'drugs_advice_duration',
  [HE_DRUG_ADVICE_FREQUENCY]: 'drugs_advice_frequency',
  [HE_DRUG_ADVICE_TIMING]: 'drugs_advice_when',
  [HE_DRUG_ADVICE_TYPE]: 'drugs_types',
  [HE_DRUG_ADVICE_NOTE]: 'drugs_advice_notes',
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function waitForBrowserIdle() {
  return new Promise((resolve) => {
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => resolve(), { timeout: 8000 })
    } else {
      setTimeout(resolve, 4000)
    }
  })
}

function waitUntilOnline() {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || navigator.onLine !== false) {
      resolve()
      return
    }
    window.addEventListener('online', () => resolve(), { once: true })
  })
}

/**
 * Logged-in account's authorities, parse-safe. Deliberately NOT the layout's
 * getUserRoles(): that helper logs the user out and reloads the page on a parse
 * failure, which a background sync must never do.
 */
function getAuthoritiesSafe() {
  try {
    const parsed = JSON.parse(localStorage.getItem('authorities'))
    return Array.isArray(parsed) ? parsed : []
  } catch (error) {
    return []
  }
}

// user-initiated requests always win: the sync counts as background traffic and
// waits for the foreground network to go idle before every page it pulls
installAxiosNetworkTracking()

async function fetchPageWithRetry(url) {
  let lastError
  for (let attempt = 1; attempt <= MAX_PAGE_ATTEMPTS; attempt++) {
    try {
      await waitUntilOnline()
      await waitForForegroundNetworkIdle()
      const response = await getCPDataWithoutParams(url, {
        headers: { [BACKGROUND_REQUEST_HEADER]: '1' },
      })
      if (response?.status === 200 && Array.isArray(response?.data)) {
        return response.data
      }
      lastError = new Error(`Unexpected response (status ${response?.status}) for ${url}`)
      // 403 = this account's roles aren't allowed this listing (e.g. therapist accounts
      // and /api/secure/drug/list, which needs a doctor/pharmacist/clinic role) — it is
      // a deliberate server-side denial, so retrying cannot change the answer
      if (response?.status === 403) {
        lastError.forbidden = true
        break
      }
    } catch (error) {
      lastError = error
      if (error?.response?.status === 403) {
        lastError.forbidden = true
        break
      }
    }
    if (attempt < MAX_PAGE_ATTEMPTS) {
      await sleep(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1))
    }
  }
  throw lastError
}

/** Labels + underlying IndexedDB stores per sync unit — drives the settings panel and per-table reset. */
export const EMR_SYNC_STORE_META = {
  drugs: { label: 'Drugs', idbStoreNames: ['drugs'] },
  complaints: { label: 'Complaints', idbStoreNames: ['health_complaints'] },
  diagnosis: { label: 'Diagnosis', idbStoreNames: ['diagnosis'] },
  observations: { label: 'Observations', idbStoreNames: ['observations'] },
  advices: { label: 'Advices', idbStoreNames: ['advice'] },
  instruction: { label: 'Instructions', idbStoreNames: ['instructions'] },
  investigation: { label: 'Investigations', idbStoreNames: ['investigation'] },
  patients: { label: 'Patients', idbStoreNames: ['patients'] },
  psychAssessments: { label: 'Psych Assessments', idbStoreNames: ['psych_assessments'] },
  genericDrugs: { label: 'Generic Drugs', idbStoreNames: ['generic_drugs'] },
  clinicalDiagnosis: { label: 'Clinical Diagnoses (ICD-10)', idbStoreNames: ['clinical_diagnosis'] },
  clinicalSymptoms: { label: 'Clinical Symptoms', idbStoreNames: ['clinical_symptoms'] },
  clinicalDescriptors: { label: 'Clinical Descriptors', idbStoreNames: ['clinical_descriptors'] },
  drugAdviceEntities: {
    label: 'Rx Advice Options',
    idbStoreNames: [
      'drug_advice_dosage',
      'drug_advice_duration',
      'drug_advice_frequency',
      'drug_advice_timing',
      'drug_advice_type',
      'drug_advice_note',
    ],
  },
  prescriptionIntel: {
    label: 'Smart Suggestions (most-used per section)',
    idbStoreNames: ['prescription_intel'],
  },
}

const INTEL_LAST_SYNCED_KEY = 'prescriptionIntelLastUpdatedInIdb'

function percentFor(page, totalPages) {
  // integer percent, clamped to 99 while running — completion alone reports 100
  return Math.min(99, Math.max(0, Math.floor((page * 100) / Math.max(totalPages, page, 1))))
}

async function syncStoreForBrand({ definition, clinicBrandId, modifiedAfter, onProgress }) {
  const isFullSync = !modifiedAfter
  const cursorKey = cursorStorageKey(definition.progressKey, clinicBrandId)
  let startedAtUtc = dayjs.utc().format(STORAGE_TIME_FORMAT)
  let page = 1
  let records_total = 0

  // clean resume: an interrupted FULL sync left a cursor — continue from the next
  // page (already-written pages are upserts, so nothing is lost or duplicated) and
  // keep the ORIGINAL start stamp so the incremental window stays correct
  if (isFullSync) {
    const cursor = readJsonFromStorage(cursorKey)
    if (cursor?.nextPage > 1 && cursor?.startedAtUtc) {
      page = cursor.nextPage
      startedAtUtc = cursor.startedAtUtc
      records_total = cursor.records || 0
    }
  }

  const knownPages = readJsonFromStorage(knownPagesStorageKey(definition.progressKey, clinicBrandId))
  const totalPagesGuess = () => knownPages || definition.estimatedPages || 50

  while (true) {
    if (isEmrSyncStorePaused(definition.progressKey)) {
      const pausedError = new Error(`${definition.progressKey} paused from settings`)
      pausedError.paused = true
      throw pausedError // cursor already persisted — resume picks up right here
    }
    const modifiedAfterParam = modifiedAfter
      ? `&modifiedAfter=${dayjs.utc(modifiedAfter).format('YYYY-MM-DDTHH:mm:ss')}Z`
      : ''
    const url = `${definition.listPath}?search=&clinicBrandId=${clinicBrandId}&page=${page}&pageSize=${PAGE_SIZE}${modifiedAfterParam}`
    const records = await fetchPageWithRetry(url)
    if (records.length === 0) {
      break
    }
    await definition.writeFn(records)
    records_total += records.length
    if (isFullSync) {
      writeJsonToStorage(cursorKey, { nextPage: page + 1, startedAtUtc, records: records_total })
    }
    const percent = percentFor(page, totalPagesGuess())
    emitStoreState(definition.progressKey, {
      status: 'syncing',
      clinicBrandId,
      page,
      totalPages: totalPagesGuess(),
      records: records_total,
      percent,
      mode: isFullSync ? 'full' : 'incremental',
    })
    onProgress?.(`${definition.progressKey}-${clinicBrandId}`, percent)
    page += 1
    // breathe between pages so a long first sync never hogs the event loop
    await sleep(INTER_PAGE_DELAY_MS)
  }

  if (isFullSync) {
    localStorage.removeItem(cursorKey)
    // remember the real page count so the next run's progress bar is accurate
    writeJsonToStorage(
      knownPagesStorageKey(definition.progressKey, clinicBrandId),
      Math.max(page - 1, 1),
    )
  }
  onProgress?.(`${definition.progressKey}-${clinicBrandId}`, 100)
  return { pages: page - 1, records: records_total, startedAtUtc }
}

/**
 * Server-side last-update times per document type (max across brands). A failed
 * status call leaves the type unknown, which we treat the same way the legacy sync
 * did: skip when we already have local data, sync when we have none.
 */
async function getMongoLastUpdatedByDocumentType(clinicBrandIds) {
  const byType = {}
  await Promise.all(
    clinicBrandIds.map(async (clinicBrandId) => {
      try {
        const response = await getMPDataWithoutParams(
          `/api/secure/doctor/medical-document-update-status/list?clinicBrandId=${clinicBrandId}`,
        )
        if (response?.status === 200 && Array.isArray(response?.data)) {
          response.data.forEach((item) => {
            const lastUpdateTime = item?.lastUpdateTimeUtc
            if (!item?.documentType || !lastUpdateTime) {
              return
            }
            if (
              !byType[item.documentType] ||
              dayjs.utc(lastUpdateTime).isAfter(dayjs.utc(byType[item.documentType]))
            ) {
              byType[item.documentType] = lastUpdateTime
            }
          })
        }
      } catch (error) {
        console.warn('Could not read medical-document-update-status for', clinicBrandId, error)
      }
    }),
  )
  return byType
}

// --- Server-driven sync (Phase 2). The practitioner's resolved EMR sync policy (GET
// /api/secure/emr/sync-config) decides whether the local IndexedDB sync runs at all, which
// collections it covers, and which implementation the client uses. When it selects SYNC_DELTA we run
// the consolidated delta endpoint instead of the legacy per-store fan-out. Absent/LEGACY policy (or
// an undeployed endpoint) falls through to legacy, so this ships safely ahead of the backend + rollout.
let cachedSyncConfig = null
let cachedSyncConfigAt = 0
const SYNC_CONFIG_TTL_MS = 10 * 60 * 1000

async function getEmrSyncConfig() {
  if (cachedSyncConfig && Date.now() - cachedSyncConfigAt < SYNC_CONFIG_TTL_MS) {
    return cachedSyncConfig
  }
  try {
    const response = await getMPDataWithoutParams('/api/secure/emr/sync-config')
    const config = getDataMapFromCommonResponse(response)
    if (config) {
      cachedSyncConfig = config
      cachedSyncConfigAt = Date.now()
    }
    return config || cachedSyncConfig
  } catch (error) {
    return cachedSyncConfig // network / 404 (endpoint not deployed) -> caller falls back to legacy
  }
}

// documentType (server Mongo collection) -> { write(records), delete(ids), storageKey }. Reuses the
// existing writeFns + per-store localStorage cursor keys so cursors stay interchangeable with legacy.
function buildDeltaHandlers() {
  const handlers = {}
  for (const definition of SYNC_DEFINITIONS) {
    if (!definition.documentType) {
      continue
    }
    const storeNames = EMR_SYNC_STORE_META[definition.progressKey]?.idbStoreNames || []
    handlers[definition.documentType] = {
      write: (records) => definition.writeFn(records),
      delete: (ids) => Promise.all(storeNames.map((storeName) => deleteRecordsFromStore(storeName, ids))),
      storageKey: definition.storageKey,
    }
  }
  for (const [entityParam, documentType] of Object.entries(DRUG_ADVICE_PARAM_TO_DOCUMENT_TYPE)) {
    handlers[documentType] = {
      write: (records) => addOrUpdateDrugAdviceEntitiesInIdb(entityParam, records),
      delete: (ids) => deleteDrugAdviceEntitiesFromIdb(entityParam, ids),
      storageKey: KEYS.DRUG_ADVICE_ENTITIES,
    }
  }
  return handlers
}

const MAX_DELTA_ROUNDS = 60

async function runEmrSyncViaDelta({ enabledCollections, force, onProgress }) {
  await createERMDatabase()
  const handlers = buildDeltaHandlers()
  const documentTypes = (enabledCollections || []).filter((documentType) => handlers[documentType])
  const synced = []
  const deferredToLegacy = new Set()

  for (let round = 0; round < MAX_DELTA_ROUNDS; round += 1) {
    const cursors = {}
    for (const documentType of documentTypes) {
      cursors[documentType] = force
        ? null
        : localStorage.getItem(handlers[documentType].storageKey) || null
    }

    let data
    try {
      const response = await postMPDataWithoutParams('/api/secure/emr/sync-delta', {
        cursors,
        pageSize: PAGE_SIZE,
      })
      data = getDataMapFromCommonResponse(response)
    } catch (error) {
      console.warn('EMR sync-delta request failed', error)
      break
    }
    if (!data || data.syncEnabled === false) {
      break
    }

    let anyHasMore = false
    for (const [documentType, delta] of Object.entries(data.stores || {})) {
      const handler = handlers[documentType]
      if (!handler) {
        continue
      }
      try {
        if (delta.records?.length) {
          await handler.write(delta.records)
        }
        if (delta.deleted?.length) {
          await handler.delete(delta.deleted)
        }
        if (delta.cursor) {
          localStorage.setItem(handler.storageKey, delta.cursor)
        }
        if (delta.hasMore) {
          anyHasMore = true
        }
        if (!synced.includes(documentType)) {
          synced.push(documentType)
        }
      } catch (error) {
        console.warn(`EMR sync-delta write failed for ${documentType}`, error)
      }
    }
    for (const documentType of data.unchanged || []) {
      const handler = handlers[documentType]
      if (handler && data.serverTimeUtc) {
        localStorage.setItem(handler.storageKey, data.serverTimeUtc)
      }
    }
    ;(data.legacyFallback || []).forEach((documentType) => deferredToLegacy.add(documentType))
    onProgress?.('sync-delta', anyHasMore ? 50 : 100)
    if (!anyHasMore) {
      break
    }
  }

  if (deferredToLegacy.size > 0) {
    console.warn('EMR sync-delta: collections not served by the endpoint yet:', [...deferredToLegacy])
  }
  return { deltaRun: true, synced }
}

async function runEmrSync({ clinicBrandIds, force, onProgress, onlyStores }) {
  const runStartedAt = Date.now()

  // Server-driven policy: skip if disabled, or run the consolidated delta path if the resolved
  // policy selects it. Scoped per-store runs (onlyStores) always use the legacy path.
  if (!onlyStores?.length) {
    const syncConfig = await getEmrSyncConfig()
    if (syncConfig?.syncEnabled === false) {
      return { skippedRun: 'sync-disabled' }
    }
    if (syncConfig?.implementation === 'SYNC_DELTA') {
      return runEmrSyncViaDelta({
        enabledCollections: syncConfig.enabledCollections,
        force,
        onProgress,
      })
    }
  }
  // a full wipe would destroy the pages an interrupted run already downloaded — when
  // any resume cursor is outstanding, force keeps the data and resumes instead.
  // Scoped runs (onlyStores) never wipe the whole DB; per-table reset clears its own store.
  if (force && !onlyStores?.length && !anyResumeCursorExists()) {
    await deleteERMDatabase()
    clearDecryptedStoreCache()
  }
  // opening the DB first matters: a pending version upgrade wipes the stores and
  // clears the last-updated keys, and that must happen BEFORE we read those keys
  await createERMDatabase()

  const mongoLastByType = force ? {} : await getMongoLastUpdatedByDocumentType(clinicBrandIds)
  const failures = []
  const synced = []
  const skipped = []
  const storeStats = []

  for (const definition of SYNC_DEFINITIONS) {
    if (onlyStores?.length && !onlyStores.includes(definition.progressKey)) {
      continue
    }
    // role-gated stores are not even attempted for accounts that can't pass the
    // endpoint's @PreAuthorize — e.g. drugs sync only runs for ROLE_DOCTOR
    if (
      definition.requiredRoles?.length &&
      !definition.requiredRoles.some((role) => getAuthoritiesSafe().includes(role))
    ) {
      skipped.push(`${definition.progressKey} (role not applicable)`)
      emitStoreState(definition.progressKey, { status: 'not-applicable' })
      continue
    }
    if (isEmrSyncStorePaused(definition.progressKey)) {
      skipped.push(`${definition.progressKey} (paused)`)
      emitStoreState(definition.progressKey, { status: 'paused' })
      continue
    }
    const lastSyncedLocal = force ? null : localStorage.getItem(definition.storageKey)
    let modifiedAfter = null
    if (lastSyncedLocal) {
      const mongoLast = mongoLastByType[definition.documentType]
      if (!mongoLast || !dayjs.utc(mongoLast).isAfter(dayjs.utc(lastSyncedLocal))) {
        skipped.push(definition.progressKey)
        emitStoreState(definition.progressKey, { status: 'fresh', percent: 100 })
        continue
      }
      modifiedAfter = lastSyncedLocal
    }

    const storeStartedAt = Date.now()
    let pages = 0
    let records = 0
    // resumed brands keep their ORIGINAL start stamp (from the cursor); advancing the
    // store timestamp to the earliest of them keeps the next incremental window sound
    let earliestStartedAtUtc = null
    try {
      for (const clinicBrandId of clinicBrandIds) {
        const brandResult = await syncStoreForBrand({
          definition,
          clinicBrandId,
          modifiedAfter,
          onProgress,
        })
        pages += brandResult?.pages || 0
        records += brandResult?.records || 0
        if (
          !earliestStartedAtUtc ||
          dayjs.utc(brandResult?.startedAtUtc).isBefore(dayjs.utc(earliestStartedAtUtc))
        ) {
          earliestStartedAtUtc = brandResult?.startedAtUtc
        }
      }
      localStorage.setItem(
        definition.storageKey,
        earliestStartedAtUtc || dayjs.utc().format(STORAGE_TIME_FORMAT),
      )
      synced.push(definition.progressKey)
      emitStoreState(definition.progressKey, {
        status: 'synced',
        percent: 100,
        records,
        pagesDone: pages,
      })
      storeStats.push({
        store: definition.progressKey,
        status: 'synced',
        mode: modifiedAfter ? 'incremental' : 'full',
        pages,
        records,
        durationMs: Date.now() - storeStartedAt,
      })
    } catch (error) {
      if (error?.paused) {
        // user paused mid-run: cursor is saved, nothing lost — resume continues here
        skipped.push(`${definition.progressKey} (paused mid-sync)`)
        emitStoreState(definition.progressKey, { status: 'paused', records })
        storeStats.push({
          store: definition.progressKey,
          status: 'paused',
          mode: modifiedAfter ? 'incremental' : 'full',
          pages,
          records,
          durationMs: Date.now() - storeStartedAt,
        })
        continue
      }
      if (error?.forbidden) {
        // by-design role denial (e.g. therapists can't list drugs) — not a failure:
        // no retry loop, no failure alert; re-checked cheaply on later periodic runs
        skipped.push(`${definition.progressKey} (not permitted)`)
        storeStats.push({
          store: definition.progressKey,
          status: 'skipped',
          mode: modifiedAfter ? 'incremental' : 'full',
          pages,
          records,
          durationMs: Date.now() - storeStartedAt,
          error: 'not permitted for this account (403)',
        })
        continue
      }
      // timestamp deliberately NOT advanced — the next run re-syncs this window
      console.warn(`EMR background sync failed for ${definition.progressKey}`, error)
      failures.push({ store: definition.progressKey, error: error?.message })
      emitStoreState(definition.progressKey, {
        status: 'failed',
        error: error?.message,
        records,
      })
      storeStats.push({
        store: definition.progressKey,
        status: 'failed',
        mode: modifiedAfter ? 'incremental' : 'full',
        pages,
        records,
        durationMs: Date.now() - storeStartedAt,
        error: error?.message,
      })
    }
  }

  // Rx dropdown value stores (dosage/duration/frequency/timing/type/notes) — small
  // lists, synced incrementally on every run under one shared timestamp
  const adviceLastSynced = force ? null : localStorage.getItem(KEYS.DRUG_ADVICE_ENTITIES)
  const adviceStartedAtUtc = dayjs.utc().format(STORAGE_TIME_FORMAT)
  // Gate the Rx-dropdown group by the manifest like every other store. Fallback: if the backend
  // doesn't yet track the drugs_advice_* collections (pre-deploy — none appear in mongoLastByType),
  // sync anyway (the immediate-run throttle bounds it) rather than silently stop refreshing them.
  const adviceDocumentTypes = Object.values(DRUG_ADVICE_PARAM_TO_DOCUMENT_TYPE)
  const adviceTrackedInManifest = adviceDocumentTypes.some(
    (documentType) => mongoLastByType[documentType] != null,
  )
  const adviceIsFresh =
    !!adviceLastSynced &&
    adviceTrackedInManifest &&
    !adviceDocumentTypes.some((documentType) => {
      const mongoLast = mongoLastByType[documentType]
      return mongoLast && dayjs.utc(mongoLast).isAfter(dayjs.utc(adviceLastSynced))
    })
  const skipAdvice =
    (onlyStores?.length && !onlyStores.includes('drugAdviceEntities')) ||
    isEmrSyncStorePaused('drugAdviceEntities') ||
    adviceIsFresh
  try {
    if (skipAdvice) {
      if (adviceIsFresh) {
        skipped.push('drugAdviceEntities')
        emitStoreState('drugAdviceEntities', { status: 'fresh', percent: 100 })
      } else {
        skipped.push('drugAdviceEntities (paused or out of scope)')
      }
      throw Object.assign(new Error('advice sync skipped'), { adviceSkipped: true })
    }
    emitStoreState('drugAdviceEntities', { status: 'syncing', percent: 0 })
    let adviceRecords = 0
    for (const clinicBrandId of clinicBrandIds) {
      for (const entityParam of DRUG_ADVICE_ENTITY_PARAMS_WITH_IDB_STORE) {
        const adviceResult = await syncStoreForBrand({
          definition: {
            progressKey: entityParam,
            listPath: `/api/secure/clinic-practitioner/${entityParam}/list`,
            writeFn: (items) => addOrUpdateDrugAdviceEntitiesInIdb(entityParam, items),
            estimatedPages: 2,
          },
          clinicBrandId,
          modifiedAfter: adviceLastSynced,
          onProgress,
        })
        adviceRecords += adviceResult?.records || 0
      }
    }
    localStorage.setItem(KEYS.DRUG_ADVICE_ENTITIES, adviceStartedAtUtc)
    synced.push('drugAdviceEntities')
    emitStoreState('drugAdviceEntities', { status: 'synced', percent: 100, records: adviceRecords })
  } catch (error) {
    if (error?.adviceSkipped) {
      // deliberate skip — recorded above, not a failure
    } else
    if (error?.forbidden) {
      skipped.push('drugAdviceEntities (not permitted)')
      storeStats.push({
        store: 'drugAdviceEntities',
        status: 'skipped',
        error: 'not permitted for this account (403)',
      })
    } else {
      console.warn('EMR background sync failed for drug advice entities', error)
      failures.push({ store: 'drugAdviceEntities', error: error?.message })
      storeStats.push({ store: 'drugAdviceEntities', status: 'failed', error: error?.message })
    }
  }

  // Smart Suggestions: prime the context-free most-used set for every EMR section so
  // any blank dropdown opens instantly. Not a paged list — a bounded set of small,
  // polite (foreground-yielding) fetches; skips still-fresh caches unless forced.
  const skipIntel =
    (onlyStores?.length && !onlyStores.includes('prescriptionIntel')) ||
    isEmrSyncStorePaused('prescriptionIntel')
  if (skipIntel) {
    skipped.push('prescriptionIntel (paused or out of scope)')
    if (isEmrSyncStorePaused('prescriptionIntel')) {
      emitStoreState('prescriptionIntel', { status: 'paused' })
    }
  } else {
    try {
      emitStoreState('prescriptionIntel', { status: 'syncing', percent: 0 })
      const intelResult = await primeEmrIntelForSync({
        clinicBrandIds,
        force,
        onProgress: (doneCount, totalCount) =>
          emitStoreState('prescriptionIntel', {
            status: 'syncing',
            page: doneCount,
            totalPages: totalCount,
            percent: Math.min(99, Math.floor((doneCount * 100) / Math.max(totalCount, 1))),
          }),
      })
      localStorage.setItem(INTEL_LAST_SYNCED_KEY, dayjs.utc().format(STORAGE_TIME_FORMAT))
      synced.push('prescriptionIntel')
      emitStoreState('prescriptionIntel', {
        status: 'synced',
        percent: 100,
        records: intelResult?.primed || 0,
      })
    } catch (error) {
      console.warn('EMR background sync failed for prescription intel', error)
      failures.push({ store: 'prescriptionIntel', error: error?.message })
      emitStoreState('prescriptionIntel', { status: 'failed', error: error?.message })
    }
  }

  const result = {
    synced,
    skipped,
    failures,
    storeStats,
    force,
    durationMs: Date.now() - runStartedAt,
  }
  reportSyncRunToServer(result, clinicBrandIds)
  // warm the title-search caches off the interaction path so the first drug search
  // after a sync never pays the build cost on the doctor's keystroke
  prewarmEmrSearchCaches()
  return result
}

/**
 * Fire-and-forget admin visibility: POSTs the run summary so the server can email the
 * admin alert list (gated by the Alert Communications toggle server-side). Sent only for
 * runs worth knowing about — a full (re)fill, a forced sync, or any failure. Silent
 * periodic no-op/incremental runs stay silent, and reporting errors never affect the sync.
 */
function reportSyncRunToServer(result, clinicBrandIds) {
  try {
    const didFullFill = (result.storeStats || []).some(
      (stat) => stat.mode === 'full' && stat.records > 0,
    )
    const hasFailures = (result.failures || []).length > 0
    if (!result.force && !hasFailures && !didFullFill) {
      return
    }
    const payload = {
      trigger: result.force ? 'manual-force' : didFullFill ? 'initial-or-refill' : 'periodic',
      dbVersion: ERM_DB_VERSION,
      clinicBrandIds,
      durationMs: result.durationMs,
      syncedStores: result.synced,
      skippedStores: result.skipped,
      storeStats: result.storeStats,
      failures: result.failures,
      pageUrl: typeof window !== 'undefined' ? window.location?.pathname : null,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    }
    postCPDataWithoutParams('/api/secure/clinic-practitioner/emr-sync/report', payload).catch(
      () => {},
    )
  } catch (error) {
    // reporting must never break the sync itself
    console.warn('Could not report EMR sync run to server', error)
  }
}

let inFlightSync = null
let lastFullRunCompletedAt = 0
// Coalesce frequent triggers (mount/remount of the making pages as the doctor moves between
// patients, tab focus, periodic overlap) into at most one full run per window, so re-opening the
// session/report page doesn't re-fetch the manifest + changed stores + advice dropdowns every time.
// The periodic re-sync (PERIODIC_RESYNC_INTERVAL_MS), force (user resync) and per-store runs bypass it.
const IMMEDIATE_RESYNC_THROTTLE_MS = 15 * 60 * 1000

/**
 * Kicks off one background sync run. Safe to call from anywhere, any number of
 * times: concurrent calls in the same tab join the in-flight run, and the Web Locks
 * guard makes a second tab skip instead of double-syncing.
 */
export function startEmrBackgroundSync({
  clinicBrandIds,
  force = false,
  waitForIdleFirst = true,
  onProgress,
  onlyStores,
} = {}) {
  const brands = [...new Set((clinicBrandIds || []).filter(Boolean))]
  if (brands.length === 0) {
    return Promise.resolve({ skippedRun: 'no-clinic-brands' })
  }
  // A full run that finished within the throttle window is reused instead of re-fetching everything
  // again. Force (user resync) and scoped per-store runs always proceed.
  if (
    !force &&
    !onlyStores?.length &&
    lastFullRunCompletedAt &&
    Date.now() - lastFullRunCompletedAt < IMMEDIATE_RESYNC_THROTTLE_MS
  ) {
    return Promise.resolve({ skippedRun: 'throttled' })
  }
  if (inFlightSync) {
    if (force) {
      // a user-initiated full resync should not be silently swallowed by the periodic
      // run — chain it to start as soon as the in-flight run finishes. (Per-store
      // updates don't come through here — syncSingleEmrStore runs them concurrently.)
      const queued = inFlightSync
        .catch(() => {})
        .then(() =>
          startEmrBackgroundSync({ clinicBrandIds: brands, force, waitForIdleFirst: false, onProgress, onlyStores }),
        )
      return queued
    }
    return inFlightSync
  }
  inFlightSync = (async () => {
    try {
      if (waitForIdleFirst) {
        await waitForBrowserIdle()
      }
      await waitUntilOnline()

      const runner = () => runEmrSync({ clinicBrandIds: brands, force, onProgress, onlyStores })
      let result
      if (typeof navigator !== 'undefined' && navigator.locks?.request) {
        result = await navigator.locks.request(SYNC_LOCK_NAME, { ifAvailable: true }, async (lock) => {
          if (!lock) {
            return { skippedRun: 'another-tab-is-syncing' }
          }
          return await runner()
        })
      } else {
        result = await runner()
      }
      // Start the throttle window from a real, full run only — not a per-store run or an
      // another-tab skip (which didn't actually sync here).
      if (!onlyStores?.length && result?.skippedRun !== 'another-tab-is-syncing') {
        lastFullRunCompletedAt = Date.now()
      }
      return result
    } catch (error) {
      console.warn('EMR background sync failed; it will retry on the next run', error)
      return { failedRun: true, error: error?.message }
    } finally {
      inFlightSync = null
    }
  })()
  return inFlightSync
}

let periodicResyncHandle = null
let periodicBrandIds = []
let onlineListenerRegistered = false
let pendingFailureRetryHandle = null
let consecutiveFailureRetries = 0
const FAILURE_RETRY_DELAY_MS = 3 * 60 * 1000
const MAX_CONSECUTIVE_FAILURE_RETRIES = 2

/**
 * Resilience: a run that ends with per-store failures schedules ONE early retry
 * (3 min, capped at 2 consecutive) instead of waiting for the 30-min tick; per-store
 * timestamps only advance on full success, so the retry re-covers exactly the missed
 * window. Regaining connectivity also kicks a run immediately.
 */
function scheduleRetryIfRunFailed(runResultPromise) {
  runResultPromise
    ?.then((result) => {
      const hadFailures = (result?.failures?.length || 0) > 0 || result?.failedRun
      if (!hadFailures) {
        consecutiveFailureRetries = 0
        return
      }
      if (
        pendingFailureRetryHandle ||
        consecutiveFailureRetries >= MAX_CONSECUTIVE_FAILURE_RETRIES ||
        periodicBrandIds.length === 0
      ) {
        return
      }
      consecutiveFailureRetries += 1
      pendingFailureRetryHandle = setTimeout(() => {
        pendingFailureRetryHandle = null
        scheduleRetryIfRunFailed(
          startEmrBackgroundSync({ clinicBrandIds: periodicBrandIds, waitForIdleFirst: false }),
        )
      }, FAILURE_RETRY_DELAY_MS)
    })
    .catch(() => {})
}

/**
 * Starts an immediate background sync and keeps re-syncing on an interval while the
 * app stays open, so the local data tracks the server without user action. Repeat
 * calls just refresh the brand list the interval works with. Also retries early after
 * failed runs and re-kicks when the browser comes back online.
 */
export function ensurePeriodicEmrBackgroundSync({ clinicBrandIds } = {}) {
  // union rather than replace: the portal shell registers all brands while a making page
  // (live session / remote check-in) may only know its own — never narrow the list
  periodicBrandIds = [
    ...new Set([...periodicBrandIds, ...(clinicBrandIds || []).filter(Boolean)]),
  ]
  scheduleRetryIfRunFailed(startEmrBackgroundSync({ clinicBrandIds: periodicBrandIds }))
  if (!onlineListenerRegistered && typeof window !== 'undefined') {
    onlineListenerRegistered = true
    window.addEventListener('online', () => {
      scheduleRetryIfRunFailed(
        startEmrBackgroundSync({ clinicBrandIds: periodicBrandIds, waitForIdleFirst: false }),
      )
    })
  }
  if (periodicResyncHandle) {
    return
  }
  periodicResyncHandle = setInterval(() => {
    scheduleRetryIfRunFailed(
      startEmrBackgroundSync({ clinicBrandIds: periodicBrandIds, waitForIdleFirst: false }),
    )
  }, PERIODIC_RESYNC_INTERVAL_MS)
}

/**
 * Clinic brands the periodic sync currently covers — the remote-debug executor reuses
 * them when a support agent triggers a forced resync so it syncs exactly what the
 * portal itself would.
 */
export function getKnownEmrSyncBrandIds() {
  return [...periodicBrandIds]
}

/** Definition lookup for the settings panel (labels come from EMR_SYNC_STORE_META). */
function definitionFor(progressKey) {
  return SYNC_DEFINITIONS.find((definition) => definition.progressKey === progressKey) || null
}

// scoped per-store runs deliberately BYPASS the global single-flight: updating
// Complaints must not queue behind a long-running Drugs sync. One in-flight run per
// store per tab; a per-store Web Lock prevents the same store syncing in two tabs.
// Overlap with the periodic full run is safe — writes are idempotent upserts.
const inFlightStoreSyncs = new Map()

/**
 * Per-table update: re-syncs ONE store, concurrently with other tables and with the
 * background run. Resumes from its cursor when a previous run was interrupted; force
 * re-downloads even if the local copy looks fresh.
 */
export function syncSingleEmrStore({ progressKey, clinicBrandIds, force = true, onProgress } = {}) {
  const brands = clinicBrandIds?.length ? clinicBrandIds : getKnownEmrSyncBrandIds()
  if (brands.length === 0) {
    return Promise.resolve({ skippedRun: 'no-clinic-brands' })
  }
  resumeEmrSyncStore(progressKey) // an explicit update always un-pauses the table
  if (inFlightStoreSyncs.has(progressKey)) {
    return inFlightStoreSyncs.get(progressKey)
  }
  const run = (async () => {
    try {
      await waitUntilOnline()
      const runner = () =>
        runEmrSync({ clinicBrandIds: brands, force, onProgress, onlyStores: [progressKey] })
      if (typeof navigator !== 'undefined' && navigator.locks?.request) {
        return await navigator.locks.request(
          `${SYNC_LOCK_NAME}-store-${progressKey}`,
          { ifAvailable: true },
          async (lock) => {
            if (!lock) {
              return { skippedRun: 'store-syncing-in-another-tab' }
            }
            return await runner()
          },
        )
      }
      return await runner()
    } catch (error) {
      console.warn(`Scoped EMR sync failed for ${progressKey}`, error)
      return { failedRun: true, error: error?.message }
    } finally {
      inFlightStoreSyncs.delete(progressKey)
    }
  })()
  inFlightStoreSyncs.set(progressKey, run)
  return run
}

/**
 * Per-table reset: empties the table's IndexedDB store(s), clears its sync timestamp,
 * resume cursor and remembered page totals, then re-downloads just that table.
 */
export async function resetEmrStoreAndResync({ progressKey, clinicBrandIds, onProgress } = {}) {
  const brands = clinicBrandIds?.length ? clinicBrandIds : getKnownEmrSyncBrandIds()
  const meta = EMR_SYNC_STORE_META[progressKey]
  emitStoreState(progressKey, { status: 'resetting', percent: 0, records: 0 })
  for (const storeName of meta?.idbStoreNames || []) {
    try {
      await clearStoreInIdb(storeName)
    } catch (error) {
      console.warn(`Could not clear IDB store ${storeName}`, error)
    }
  }
  if (progressKey === 'drugAdviceEntities') {
    localStorage.removeItem(KEYS.DRUG_ADVICE_ENTITIES)
  } else if (progressKey === 'prescriptionIntel') {
    localStorage.removeItem(INTEL_LAST_SYNCED_KEY)
  } else {
    const definition = definitionFor(progressKey)
    if (definition) {
      clearStoreSyncMarkers(definition, brands)
    }
  }
  return syncSingleEmrStore({ progressKey, clinicBrandIds: brands, force: true, onProgress })
}

/**
 * Mount-point hook for pages that need the local EMR data regardless of which shell
 * rendered them (mentor portal, /l/* live sessions, remote check-in sessions). Idempotent:
 * single-flight + Web Locks + brand-union mean extra callers never duplicate work.
 */
export function useEnsureEmrBackgroundSync(clinicBrandId) {
  useEffect(() => {
    if (clinicBrandId) {
      ensurePeriodicEmrBackgroundSync({ clinicBrandIds: [clinicBrandId] })
    }
  }, [clinicBrandId])
}
